import { DEFAULT_BACKGROUND_QUERY } from '@/pages/AdvancedEditor/constants';
import { InputGroup } from '@blueprintjs/core';
import { Search } from '@blueprintjs/icons';
import styled from '@emotion/styled';
import { debounce } from 'lodash';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { StoreType } from 'polotno/model/store';
import { ImagesGrid } from 'polotno/side-panel/images-grid';
import { unsplashDownload, unsplashList } from 'polotno/utils/api';
import { t } from 'polotno/utils/l10n';
import { useInfiniteAPI } from 'polotno/utils/use-api';
import { useEffect, useMemo, useRef } from 'react';

const colors = observable([
    'white',
    'rgb(82, 113, 255)',
    'rgb(255, 145, 77)',
    'rgb(126, 217, 87)',
    'rgb(255, 222, 89)',
    'rgb(203, 108, 230)',
    'rgb(0, 0, 0, 0)',
]);

const DefaultBackgroundsPanelContainer = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;

    // bg tile
    .polotno-close-panel {
        display: flex;
        aspect-ratio: 1 / 1;
        border-radius: 10px;
        padding: 0px;

        > div {
            display: flex;
            width: 100%;
            height: 100%;
            border-radius: 10px;

            :hover {
                outline: 2px solid ${({ theme }) => theme.colors.primary.main};
                outline-offset: -1px;
            }

            > img {
                display: flex;
                width: 100% !important;
                height: 100%;
                border-radius: 10px;
                object-fit: cover;
                object-position: center;
            }

            > div {
                display: none !important;
            }
        }
    }
`;

const ImagesGridStyleWrapper = styled.div`
    > div {
        display: flex !important;
        width: 100% !important;
        flex-wrap: wrap;
        gap: 12px;
        padding: 2px;

        > div {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 12px !important;
            width: 100% !important;
            flex-wrap: wrap;

            > div:nth-child(3n + 1):nth-last-child(1),
            > div:nth-child(3n + 1):nth-last-child(2),
            > div:nth-child(3n + 2):nth-last-child(1) {
                display: none;
            }
        }
    }
`;

const StyledInputGroup = styled(InputGroup)`
    position: sticky;
    top: 35px;
    z-index: 1;
    background-color: white;
    display: flex;
    height: 40px !important;
    max-height: 40px;
    align-items: center;
    gap: 8px;
    flex: 1 0 0;
    background: ${({ theme }) => theme.colors.black[100]};
    border-radius: 10px;
    margin-bottom: 16px;

    > span.bp5-icon.bp5-icon-search {
        width: 16px;
        height: 16px;
        margin: 12px 0px 0px 12px;
        color: ${({ theme }) => theme.colors.black[500]};

        :focus {
            outline: 0;
        }
    }

    :after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        background-color: white;
        z-index: -1;
        height: 56px;
    }

    .bp5-input {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1 0 0;
        background: ${({ theme }) => theme.colors.black[100]};
        border-radius: 10px;
        margin-bottom: 0px !important;
        box-shadow: none !important;
        height: 40px;

        > input {
            color: ${({ theme }) => theme.colors.black[500]};
            font-family: ${({ theme }) => theme.fonts.primary};
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: 18px;
            letter-spacing: -0.28px;
            align-self: stretch;
            margin-bottom: 0px !important;
            box-shadow: none !important;
            padding: 12px;
        }
    }

    .bp5-input:not(:first-child) {
        padding-left: 40px;
    }
`;

export const setBackgroundColorsPreset = (newColors: string[]) => {
    colors.replace(newColors);
};

export const DefaultBackgroundsPanel = observer(({ store }: { store: StoreType }) => {
    const endRef = useRef<HTMLSpanElement>(null);

    const { setQuery, loadMore, isReachingEnd, data, isLoading, error } = useInfiniteAPI({
        defaultQuery: DEFAULT_BACKGROUND_QUERY,
        getAPI: ({ page, query }) => unsplashList({ page, query }),
    });

    const debouncedLoadMore = debounce(loadMore, 500);

    const images = useMemo(
        () =>
            data
                ?.map(item => item.results)
                .flat()
                .filter(Boolean),
        [data]
    );

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !isReachingEnd && !isLoading && !!images.length) {
                    debouncedLoadMore();
                }
            },
            {
                root: null,
                rootMargin: '300px',
                threshold: 0,
            }
        );

        if (endRef.current) {
            observer.observe(endRef.current);
        }

        return () => {
            if (endRef.current) {
                observer.unobserve(endRef.current);
            }
        };
    }, [debouncedLoadMore, isReachingEnd, isLoading, images?.length]);

    return (
        <DefaultBackgroundsPanelContainer>
            <StyledInputGroup
                leftIcon={<Search />}
                placeholder={t('sidePanel.searchPlaceholder')}
                onChange={e => {
                    setQuery(e.target.value || DEFAULT_BACKGROUND_QUERY);
                }}
                type="search"
                style={{
                    marginBottom: '20px',
                }}
            />

            <ImagesGridStyleWrapper>
                <ImagesGrid
                    images={images}
                    isLoading={isLoading}
                    getPreview={image => image.urls.small}
                    loadMore={!isReachingEnd && loadMore}
                    onSelect={async image => {
                        fetch(unsplashDownload(image.id));
                        store.activePage?.set({
                            background: image.urls.regular,
                        });
                    }}
                    error={error}
                    getCredit={image => (
                        <span>
                            Photo by{' '}
                            <a href={`https://unsplash.com/@${image.user.username}?utm_source=polotno&utm_medium=referral`} target="_blank">
                                {image.user.name}
                            </a>{' '}
                            on{' '}
                            <a href="https://unsplash.com/?utm_source=polotno&utm_medium=referral" target="_blank">
                                Unsplash
                            </a>
                        </span>
                    )}
                />
            </ImagesGridStyleWrapper>
            <span ref={endRef} />
        </DefaultBackgroundsPanelContainer>
    );
});
